import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Level } from '../../models/levels/level.model';
import { LevelsResultsResponse } from '../../models/levels/responses/api-levels-results-response.model';
import { ApiLevelsService } from '../../services/levels/api/api-levels.service';
import { AlertsService } from '../../services/alerts.service';
import { ModalsService } from '../../services/modals.service';
import { LevelAddModalComponent } from './modals/level-add-modal/level-add-modal.component';
import { LevelEditModalComponent } from './modals/level-edit-modal/level-edit-modal.component';

@Component({
  templateUrl: 'levels.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, LevelAddModalComponent, LevelEditModalComponent]
})
export class LevelsComponent implements OnInit {

  @ViewChild('addModal') addModal!: LevelAddModalComponent;
  @ViewChild('editModal') editModal!: LevelEditModalComponent;

  isLoading: boolean = false;

  levels: Level[] = [];
  resultsPerPage: number = 25;
  totalResults: number = 0;

  private static pageNumber: number = 1;

  get pageNumber() {
    return LevelsComponent.pageNumber;
  }

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;

    const visibleSet = new Set<number>();

    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);

    const sorted = Array.from(visibleSet).sort((a, b) => a - b);

    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }

    return result;
  }

  goToPage(page: number): void {
    LevelsComponent.pageNumber = page;
    this.refreshLevelsList();
  }

  constructor(
    private apiLevelsService: ApiLevelsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {
    this.refreshLevelsList();
  }

  onClickPaginationPrevious(): void {
    LevelsComponent.pageNumber--;
    this.refreshLevelsList();
  }

  onClickPaginationNext(): void {
    LevelsComponent.pageNumber++;
    this.refreshLevelsList();
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshLevelsList());
  }

  onClickEdit(level: Level): void {
    this.editModal.show(level.levelId, level.level, level.nextLevelExperienceRequired, level.nextLevelReward, () => this.refreshLevelsList());
  }

  onClickDelete(level: Level): void {
    this.modalsService.showConfirmModal(() => {
      this.apiLevelsService.deleteLevel(level.levelId).then(
        () => {
          this.alertsService.displayAlertSuccess('Level deleted !');
          this.refreshLevelsList();
        }, error => {
          this.alertsService.displayAlertError('Error deleting level : ' + error);
        }
      );
    });
  }

  private refreshLevelsList(): void {
    this.isLoading = true;

    this.apiLevelsService.getLevels(LevelsComponent.pageNumber, this.resultsPerPage).then(
      (response: LevelsResultsResponse) => {
        this.isLoading = false;
        this.levels = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading levels : ' + error);
      }
    );
  }
}
